import React, { useCallback, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { DeviceSelector } from '@/components/shared/DeviceSelectorNew';
import { FiWifi, FiEdit2, FiChevronRight, FiTrash2, FiPlus, FiClock } from 'react-icons/fi';
import { getBrandNameByType } from '@/utils/equipmentBrand';
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { setLoading } from '@/store/slices/appSlice';
import { apiService } from '@/utils/api';
import type { DeviceSetupResponse, WirelessDevice } from '@/types/deviceSetup';
import type { EquipmentItem } from '@/types/dashboard';
import PoolParamsSidebar from '@/components/ui/Sidebar/PoolParamsSidebar';
import DeviceInfoSidebar from '@/components/ui/Sidebar/DeviceInfoSidebar';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import TextField from '@mui/material/TextField';
import FirmwareUpgradeSidebar from '@/components/ui/Sidebar/FirmwareUpgradeSidebar';
import UnpairedAddonsSidebar from '@/components/ui/Sidebar/UnpairedAddonsSidebar';
import AssignEquipmentSidebar from '@/components/ui/Sidebar/AssignEquipmentSidebar';
import WirelessSwitchSidebar from '@/components/ui/Sidebar/WirelessSwitchSidebar';
import { compareVersion, isTimerHourMinuteSupported, isTimerWirelessButtonSupported } from '@/utils/firmware';
import { getWirelessBatteryPercentage, isValidWirelessUuid } from '@/utils/wirelessSwitch';
import { AuxTypeCode, canBeTimerForAssignEquipment } from '@/types/device';
import { formatAuxTimerDuration, getAuxTimerSeconds } from '@/utils/auxTimer';
import { RootState } from '@/store';

const formatMac = (id?: string) => (id ? id.replace(/_/g, ':') : '-');
const buildSSID = (id?: string) => (id ? `SplashMe_${id.replace(/_/g, '')}` : '');

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
);

const InlineRow: React.FC<{ label: string; value?: string | number | React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%] text-right">{value ?? '-'}</span>
  </div>
);

// 状态/信号颜色工具
const getConnectionClass = (connected?: boolean) => (connected ? 'text-green-600' : 'text-red-500');
const getSignalClass = (rssi?: number) => {
  if (typeof rssi !== 'number') return 'text-gray-400';
  return rssi >= -60 ? 'text-green-600' : rssi >= -70 ? 'text-yellow-500' : 'text-red-500';
};

const LEGACY_MAX_AUX_TIMER_MINUTES = 29;
const LEGACY_MAX_AUX_TIMER_SECONDS = 59;
const HM_MAX_AUX_TIMER_HOURS = 5;
const HM_MAX_AUX_TIMER_MINUTES = 59;

const parseTimerInput = (value: string, max: number) => {
  const digitsOnly = value.replace(/\D/g, '');
  if (!digitsOnly) return 0;
  return Math.max(0, Math.min(max, Number.parseInt(digitsOnly, 10) || 0));
};

const DeviceSetupPage: React.FC = () => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state: RootState) => state.theme);
  const { selectedDeviceId, onDeviceChange } = useGlobalDeviceSelector();
  const [data, setData] = React.useState<DeviceSetupResponse | null>(null);

  const [isFirmwareOpen, setIsFirmwareOpen] = React.useState<boolean>(false);

  // Paired Add-ons 弹窗状态
  const [editAddonNameOpen, setEditAddonNameOpen] = React.useState(false);
  const [editingAddon, setEditingAddon] = React.useState<WirelessDevice | null>(null);
  const [addonNameDraft, setAddonNameDraft] = React.useState('');
  const [confirmDeleteAddonOpen, setConfirmDeleteAddonOpen] = React.useState(false);
  const [deletingAddon, setDeletingAddon] = React.useState<WirelessDevice | null>(null);

  // Device Info Sidebar 状态
  const [isDeviceInfoOpen, setIsDeviceInfoOpen] = React.useState<boolean>(false);
  const [deviceName, setDeviceName] = React.useState<string>('');
  const [isPoolParamsOpen, setIsPoolParamsOpen] = React.useState<boolean>(false);
  const [isUnpairedOpen, setIsUnpairedOpen] = React.useState<boolean>(false);
  const [isAssignEquipmentOpen, setIsAssignEquipmentOpen] = React.useState<boolean>(false);
  const [isWirelessSwitchOpen, setIsWirelessSwitchOpen] = React.useState<boolean>(false);
  const [selectedWirelessSwitch, setSelectedWirelessSwitch] = React.useState<WirelessDevice | null>(null);
  // AssignEquipmentSidebar 初始上下文（aux 或 pump）
  const [assignInitialMode, setAssignInitialMode] = React.useState<'aux' | 'pump'>('aux');
  const [assignInitialSlotIndex, setAssignInitialSlotIndex] = React.useState<number | undefined>(undefined);

  // MUI Snackbar state
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [deletingItem, setDeletingItem] = React.useState<EquipmentItem | null>(null);
  const [editLabelOpen, setEditLabelOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState<EquipmentItem | null>(null);
  const [labelDraft, setLabelDraft] = React.useState('');
  const [timerDialogOpen, setTimerDialogOpen] = React.useState(false);
  const [timerEnabled, setTimerEnabled] = React.useState(false);
  const [timerHours, setTimerHours] = React.useState(0);
  const [timerMinutes, setTimerMinutes] = React.useState(0);
  const [timerSeconds, setTimerSeconds] = React.useState(0);
  const [timerEditingTarget, setTimerEditingTarget] = React.useState<{
    slot: number;
    typeCode: number;
    auxName: string;
    lightTypeMode: number;
    title: string;
  } | null>(null);

  const handleSnackbarClose = (_?: any, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };
  const ssidText = useMemo(() => buildSSID(selectedDeviceId || ''), [selectedDeviceId]);

  const latestVersion = data?.latestFirmware?.version;
  const currentVersion = data?.versionInfo?.firmware_version;
  const isOutdated = useMemo(() => compareVersion(currentVersion, latestVersion) < 0, [currentVersion, latestVersion]);
  const supportsWirelessButtons = useMemo(
    () => isTimerWirelessButtonSupported(currentVersion),
    [currentVersion],
  );
  const supportsHourMinuteTimer = useMemo(
    () => isTimerHourMinuteSupported(currentVersion),
    [currentVersion],
  );
  const timerFeatureEnabled = supportsWirelessButtons;

  const pairedExpanders = useMemo(
    () => (data?.wirelessAddOns?.paired_lora_device || []).filter((item) => isValidWirelessUuid(item.uuid) && item.type !== 1),
    [data?.wirelessAddOns?.paired_lora_device],
  );
  const pairedButtons = useMemo(
    () => (data?.wirelessAddOns?.paired_lora_device || []).filter((item) => isValidWirelessUuid(item.uuid) && item.type === 1),
    [data?.wirelessAddOns?.paired_lora_device],
  );
  const filteredUnpairedAddOns = useMemo(
    () => (data?.wirelessAddOns?.unpaired_lora_device || []).filter((item) => isValidWirelessUuid(item.uuid)),
    [data?.wirelessAddOns?.unpaired_lora_device],
  );
  const currentSelectedWirelessSwitch = useMemo(() => {
    if (!selectedWirelessSwitch?.uuid) return null;
    return pairedButtons.find((item) => item.uuid === selectedWirelessSwitch.uuid) || null;
  }, [pairedButtons, selectedWirelessSwitch?.uuid]);

  const notify = useCallback((
    severity: 'success' | 'error' | 'info' | 'warning',
    message: string,
  ) => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const assignedAuxCount = useMemo(() => {
    const items = (data?.auxAssign?.equipment || []) as unknown as EquipmentItem[];
    return items.filter((e) => (e?.type_code ?? 0) !== 0 && e.type_code !== AuxTypeCode.RS485).length;
  }, [data?.auxAssign?.equipment]);

  const canShowAddEquipment = assignedAuxCount < 22;

  useEffect(() => {
    setDeviceName(selectedDeviceId || '');
  }, [selectedDeviceId]);
  const fetchData = useCallback(async (deviceId: string) => {
    dispatch(setLoading(true));
    try {
      const res = await apiService.get<DeviceSetupResponse>(`/api/v1/iot-shadow/device/${deviceId}/deviceSetup`);
      setData(res);
    } catch (err: any) {
      console.error('Failed to fetch device setup:', err);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!selectedDeviceId) return;
    fetchData(selectedDeviceId);
  }, [selectedDeviceId, fetchData]);

  const handleDeviceChange = useCallback((deviceId: string) => {
    onDeviceChange(deviceId);
  }, [onDeviceChange]);

  const handleSaveDeviceName = async (newName: string) => {
    if (!selectedDeviceId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('No device selected');
      setSnackbarOpen(true);
      return;
    }

    const prevTag = data?.TagAndTimezone?.tag || deviceName || '';
    const currentTimezone = data?.TagAndTimezone?.timezone || data?.timeInfo?.time_zone_id || '';

    // Optimistic update
    setDeviceName(newName);
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        TagAndTimezone: {
          ...(prev.TagAndTimezone || {}),
          tag: newName,
          timezone: currentTimezone,
        },
      };
    });

    try {
      dispatch(setLoading(true));
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setTimezoneAndTag`, {
        tag: newName,
        timezone: currentTimezone,
      });

      setSnackbarSeverity('success');
      setSnackbarMessage('Device name updated');
      setSnackbarOpen(true);

      await fetchData(selectedDeviceId);
    } catch (err: any) {
      console.error('Failed to save device name:', err);
      // Revert optimistic update
      setDeviceName(prevTag);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          TagAndTimezone: {
            ...(prev.TagAndTimezone || {}),
            tag: prevTag,
            timezone: currentTimezone,
          },
        };
      });

      setSnackbarSeverity('error');
      setSnackbarMessage(err?.message || 'Failed to update device name');
      setSnackbarOpen(true);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSaveTimeZone = async (newTimeZoneId: string) => {
    const prevTz = data?.timeInfo?.time_zone_id;
    const prevTagTimezone = data?.TagAndTimezone?.timezone;

    // Optimistic update of local state
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        timeInfo: { ...prev.timeInfo, time_zone_id: newTimeZoneId },
        TagAndTimezone: {
          ...(prev.TagAndTimezone || {}),
          timezone: newTimeZoneId,
          tag: prev.TagAndTimezone?.tag ?? buildSSID(selectedDeviceId),
        },
      };
    });

    try {
      dispatch(setLoading(true));
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setTimezoneAndTag`, {
        tag: data?.TagAndTimezone?.tag ,
        timezone: newTimeZoneId,
      });

      setSnackbarSeverity('success');
      setSnackbarMessage('Time zone updated');
      setSnackbarOpen(true);

      // Refresh full device setup to update sidebar (time, network, etc.)
      await fetchData(selectedDeviceId);
    } catch (err: any) {
      console.error('Failed to save time zone:', err);
      // Revert optimistic update on failure
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          timeInfo: { ...prev.timeInfo, time_zone_id: prevTz || '' },
          TagAndTimezone: {
            ...(prev.TagAndTimezone || {}),
            timezone: prevTagTimezone || '',
            tag: prev.TagAndTimezone?.tag || buildSSID(selectedDeviceId),
          },
        };
      });

      setSnackbarSeverity('error');
      setSnackbarMessage(err?.message || 'Failed to update time zone');
      setSnackbarOpen(true);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSavePoolParams = async (newPoolSize: number, newCost: number) => {
    if (!selectedDeviceId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('No device selected');
      setSnackbarOpen(true);
      return;
    }

    const prevPoolSize = data?.poolParams?.pool_size ?? 0;
    const prevCost = data?.poolParams?.electricity_cost ?? 0;

    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        poolParams: {
          ...(prev.poolParams || { pool_size: 0, electricity_cost: 0 }),
          pool_size: newPoolSize,
          electricity_cost: newCost,
        },
      };
    });

    try {
      dispatch(setLoading(true));
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setPoolParams`, {
        pool_size: newPoolSize,
        cost: newCost,
      });

      setSnackbarSeverity('success');
      setSnackbarMessage('Pool parameters updated');
      setSnackbarOpen(true);

      await fetchData(selectedDeviceId);
    } catch (err: any) {
      console.error('Failed to save pool params:', err);
      // Revert optimistic update
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          poolParams: {
            ...(prev.poolParams || { pool_size: 0, electricity_cost: 0 }),
            pool_size: prevPoolSize,
            electricity_cost: prevCost,
          },
        };
      });

      setSnackbarSeverity('error');
      setSnackbarMessage(err?.message || 'Failed to update pool parameters');
      setSnackbarOpen(true);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleUpgradeUpload = async (fileBlob: Blob, fileName: string) => {
    if (!selectedDeviceId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('No device selected');
      setSnackbarOpen(true);
      return;
    }
    try {
      dispatch(setLoading(true));
      const formData = new FormData();
      formData.append('firmware', fileBlob, fileName);
      formData.append('filename', fileName);
      await apiService.upload(`/api/v1/iot-shadow/device/${selectedDeviceId}/firmware/upgrade`, formData);
      setSnackbarSeverity('success');
      setSnackbarMessage('Firmware upgrade request sent');
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Firmware upgrade failed:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage(err?.message || 'Firmware upgrade failed');
      setSnackbarOpen(true);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Assign Equipment 小卡片（包含 pump 作为第一个卡片）
  const renderEquipmentCards = (items: EquipmentItem[] = [], pump?: DeviceSetupResponse['pumpInfo']) => {
    const valid = items.filter((e) => {
      const isEmptyType = (e?.type_code ?? 0) === 0;
      return !(isEmptyType);
    });
    const isRs485Equipment = (item: EquipmentItem) => item.type_code === AuxTypeCode.RS485;

    const computeLightTypeMode = (item: EquipmentItem): number => {
      const plm = data?.poolLightSwitchMode;
      if (!plm) return 0;
      if (item.type_code === plm.g_pool_light_type) return plm.g_pool_light_type_mode || 0;
      if (item.type_code === plm.g_spa_light_type) return plm.g_spa_light_type_mode || 0;
      return 0;
    };

    const onEdit = (e: EquipmentItem) => {
      const currentName = data?.auxAssign?.aux_name?.[e.slot] ?? e.label ?? '';
      setEditingItem(e);
      setLabelDraft(currentName);
      setEditLabelOpen(true);
    };

    const onDelete = (e: EquipmentItem) => {
      setDeletingItem(e);
      setConfirmDeleteOpen(true);
    };

    const getExistingTimerSeconds = (slot: number) => getAuxTimerSeconds(data?.auxAssign?.aux_timer, slot);

    const openTimerEditor = ({
      slot,
      typeCode,
      auxName,
      lightTypeMode,
      title,
    }: {
      slot: number;
      typeCode: number;
      auxName: string;
      lightTypeMode: number;
      title: string;
    }) => {
      const existingSeconds = getExistingTimerSeconds(slot);
      setTimerEditingTarget({ slot, typeCode, auxName, lightTypeMode, title });
      setTimerEnabled(existingSeconds > 0);
      if (supportsHourMinuteTimer) {
        const totalMinutes = Math.floor(existingSeconds / 60);
        setTimerHours(Math.min(HM_MAX_AUX_TIMER_HOURS, Math.floor(totalMinutes / 60)));
        setTimerMinutes(Math.min(HM_MAX_AUX_TIMER_MINUTES, totalMinutes % 60));
        setTimerSeconds(0);
      } else {
        setTimerHours(0);
        setTimerMinutes(Math.min(LEGACY_MAX_AUX_TIMER_MINUTES, Math.floor(existingSeconds / 60)));
        setTimerSeconds(Math.min(LEGACY_MAX_AUX_TIMER_SECONDS, existingSeconds % 60));
      }
      setTimerDialogOpen(true);
    };

    const handleConfirmEdit = async () => {
      if (!selectedDeviceId || !editingItem) {
        setEditLabelOpen(false);
        setEditingItem(null);
        return;
      }
      try {
        dispatch(setLoading(true));
        setEditLabelOpen(false);
        await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setAuxAssign`, {
          aux_num: editingItem.slot,
          type_code: editingItem.type_code,
          aux_name: labelDraft.trim(),
          light_type_mode: computeLightTypeMode(editingItem),
          aux_timer_duration_sec: getExistingTimerSeconds(editingItem.slot),
        });
        setSnackbarSeverity('success');
        setSnackbarMessage(`Auxiliary ${editingItem.slot} label updated`);
        setSnackbarOpen(true);
        await fetchData(selectedDeviceId);
      } catch (err: any) {
        console.error('Update auxiliary label failed:', err);
        setSnackbarSeverity('error');
        setSnackbarMessage(err?.message || 'Update auxiliary label failed');
        setSnackbarOpen(true);
      } finally {
        setEditingItem(null);
        dispatch(setLoading(false));
      }
    };

    const handleConfirmDelete = async () => {
      if (!selectedDeviceId || !deletingItem) {
        setConfirmDeleteOpen(false);
        setDeletingItem(null);
        return;
      }
      try {
        dispatch(setLoading(true));
        setConfirmDeleteOpen(false);
        if (isRs485Equipment(deletingItem)) {
          await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setRs485Assign`, {
            enabled: false,
          });
        } else {
          await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setAuxAssign`, {
            aux_num: deletingItem.slot,
            type_code: 0,
            aux_name: 'Empty',
            light_type_mode: 0,
            aux_timer_duration_sec: 0,
          });
        }
        setSnackbarSeverity('success');
        setSnackbarMessage(isRs485Equipment(deletingItem) ? 'RS485 removed' : `Auxiliary ${deletingItem.slot} delete`);
        setSnackbarOpen(true);
        await fetchData(selectedDeviceId);
      } catch (err: any) {
        console.error('Delete auxiliary failed:', err);
        setSnackbarSeverity('error');
        setSnackbarMessage(err?.message || 'Delete auxiliary failed');
        setSnackbarOpen(true);
      } finally {
        setDeletingItem(null);
        dispatch(setLoading(false));
      }
    };

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pump && (
          <Card key="pump-card" header={<div className="flex items-center justify-between"><div className="font-semibold">Filter Pump</div><button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => { setAssignInitialMode('pump'); setAssignInitialSlotIndex(0); setIsAssignEquipmentOpen(true); }} aria-label="Edit pump"><FiChevronRight className="h-4 w-4" /></button></div>} className="p-3">
            <div className="space-y-2">
              <InlineRow label="Type" value={pump.pump_type || '-'} />
              <InlineRow label="Brand" value={pump.pump_brand + ' ' + pump.pump_model || '-'} />
              {timerFeatureEnabled && (
                <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 py-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Timer</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate min-w-0">
                    {formatAuxTimerDuration(getExistingTimerSeconds(0), supportsHourMinuteTimer)}
                  </span>
                  <button
                    type="button"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700"
                    onClick={() =>
                      openTimerEditor({
                        slot: 0,
                        typeCode: data?.auxAssign?.aux_assign?.[0] ?? AuxTypeCode.SPALSHME_PUMP,
                        auxName: data?.auxAssign?.aux_name?.[0] ?? 'Filter Pump',
                        lightTypeMode: 0,
                        title: 'Filter Pump',
                      })
                    }
                    aria-label="Edit timer"
                  >
                    <FiClock className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </Card>
        )}
        {valid.length === 0 && !pump && (
          <div className="text-sm text-gray-500 dark:text-gray-400">No equipment assigned.</div>
        )}
        {valid.map((e) => (
          <Card
            key={`${e.slot}-${e.type_code}-${e.label}`}
            header={
              <div className="flex items-center justify-between">
                <div className="font-semibold">{isRs485Equipment(e) ? 'RS485' : `Auxiliary ${e.slot}`}</div>
                <button
                  type="button"
                  className="inline-flex items-center text-red-500 hover:text-red-600"
                  onClick={() => onDelete(e)}
                  aria-label="Delete equipment"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
            }
            className="p-3"
          >
            <div className="space-y-2">
              {isRs485Equipment(e) ? (
                <>
                  <InlineRow label="Type" value={e.brand || 'Mineral Swim V2'} />
                  <InlineRow label="Label" value={e.label || 'Mineral Swim V2'} />
                </>
              ) : (
                <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 py-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Label</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate min-w-0">{e.label ?? '-'}</span>
                  <button
                    type="button"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700"
                    onClick={() => onEdit(e)}
                    aria-label="Edit label"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                </div>
              )}
              <InlineRow label="Function" value={getBrandNameByType(e.type_code)} />
              {timerFeatureEnabled && canBeTimerForAssignEquipment(e.type_code) && (
                <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 py-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Timer</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate min-w-0">
                    {formatAuxTimerDuration(getExistingTimerSeconds(e.slot), supportsHourMinuteTimer)}
                  </span>
                  <button
                    type="button"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700"
                    onClick={() =>
                      openTimerEditor({
                        slot: e.slot,
                        typeCode: e.type_code,
                        auxName: data?.auxAssign?.aux_name?.[e.slot] ?? e.label ?? '',
                        lightTypeMode: computeLightTypeMode(e),
                        title: e.label || `Auxiliary ${e.slot}`,
                      })
                    }
                    aria-label="Edit timer"
                  >
                    <FiClock className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </Card>
        ))}
        <ConfirmDialog
          open={confirmDeleteOpen}
          title="Delete Auxiliary?"
          onCancel={() => setConfirmDeleteOpen(false)}
          onOk={handleConfirmDelete}
          cancelText="No, Cancel"
          okText="Yes, Please"
        >
          <div className="text-sm text-gray-700 dark:text-gray-200">This will remove all settings of this auxiliary.</div>
        </ConfirmDialog>
        <ConfirmDialog
          open={timerDialogOpen}
          title="Timer Settings"
          onCancel={() => {
            setTimerDialogOpen(false);
            setTimerEditingTarget(null);
              setTimerHours(0);
              setTimerMinutes(0);
              setTimerSeconds(0);
          }}
          onOk={async () => {
            if (!selectedDeviceId || !timerEditingTarget) {
              setTimerDialogOpen(false);
              return;
            }
            const nextDuration = timerEnabled
              ? Math.max(
                  0,
                  supportsHourMinuteTimer ? timerHours * 3600 + timerMinutes * 60 : timerMinutes * 60 + timerSeconds
                )
              : 0;
            if (timerEnabled && nextDuration <= 0) return;
            try {
              dispatch(setLoading(true));
              setTimerDialogOpen(false);
              await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setAuxAssign`, {
                aux_num: timerEditingTarget.slot,
                type_code: timerEditingTarget.typeCode,
                aux_name: timerEditingTarget.auxName,
                light_type_mode: timerEditingTarget.lightTypeMode,
                aux_timer_duration_sec: nextDuration,
              });
              setSnackbarSeverity('success');
              setSnackbarMessage(`${timerEditingTarget.title} timer updated`);
              setSnackbarOpen(true);
              await fetchData(selectedDeviceId);
            } catch (err: any) {
              console.error('Update timer failed:', err);
              setSnackbarSeverity('error');
              setSnackbarMessage(err?.message || 'Update timer failed');
              setSnackbarOpen(true);
            } finally {
              setTimerEditingTarget(null);
              dispatch(setLoading(false));
            }
          }}
          cancelText="Cancel"
          okText="Save"
          fullWidth
          maxWidth="sm"
        >
          <div className="space-y-4">
            <div className="text-sm text-gray-700 dark:text-gray-200">
              Configure timer for {timerEditingTarget?.title || 'this equipment'}.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${
                  !timerEnabled
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200'
                }`}
                onClick={() => setTimerEnabled(false)}
              >
                Normal Mode
              </button>
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${
                  timerEnabled
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200'
                }`}
                onClick={() => setTimerEnabled(true)}
              >
                Timer Mode
              </button>
            </div>
            {timerEnabled && (
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1 text-sm">
                  <span className="block text-gray-500 dark:text-gray-400">
                    {supportsHourMinuteTimer ? 'Hours' : 'Minutes'}
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={supportsHourMinuteTimer ? timerHours : timerMinutes}
                    onFocus={(e) => e.currentTarget.select()}
                    onChange={(e) => {
                      const nextValue = parseTimerInput(
                        e.target.value,
                        supportsHourMinuteTimer ? HM_MAX_AUX_TIMER_HOURS : LEGACY_MAX_AUX_TIMER_MINUTES
                      );
                      if (supportsHourMinuteTimer) {
                        setTimerHours(nextValue);
                      } else {
                        setTimerMinutes(nextValue);
                      }
                    }}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="block text-gray-500 dark:text-gray-400">
                    {supportsHourMinuteTimer ? 'Minutes' : 'Seconds'}
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={supportsHourMinuteTimer ? timerMinutes : timerSeconds}
                    onFocus={(e) => e.currentTarget.select()}
                    onChange={(e) => {
                      const nextValue = parseTimerInput(
                        e.target.value,
                        supportsHourMinuteTimer ? HM_MAX_AUX_TIMER_MINUTES : LEGACY_MAX_AUX_TIMER_SECONDS
                      );
                      if (supportsHourMinuteTimer) {
                        setTimerMinutes(nextValue);
                      } else {
                        setTimerSeconds(nextValue);
                      }
                    }}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
              </div>
            )}
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Current timer: {formatAuxTimerDuration(
                timerEnabled
                  ? (supportsHourMinuteTimer ? timerHours * 3600 + timerMinutes * 60 : timerMinutes * 60 + timerSeconds)
                  : 0,
                supportsHourMinuteTimer,
              )}
            </div>
          </div>
        </ConfirmDialog>
        <ConfirmDialog
          open={editLabelOpen}
          title="Edit Auxiliary Label"
          onCancel={() => setEditLabelOpen(false)}
          onOk={handleConfirmEdit}
          cancelText="Cancel"
          okText="Save"
          fullWidth
          maxWidth="sm"
        >
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            variant="outlined"
            label="Label"
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            InputLabelProps={mode === 'dark' ? { sx: { color: '#fff' } } : undefined}
            sx={mode === 'dark' ? {
              '& .MuiInputBase-input': { color: '#fff' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#fff' },
                '&:hover fieldset': { borderColor: '#fff' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
              '& .MuiFormLabel-root': { color: '#fff' },
            } : undefined}
          />
        </ConfirmDialog>
      </div>
    );
  };

  // LoRa 名称编辑和删除逻辑（仅前端状态更新，后端集成可替换）
  const beginEditAddon = (addon: WirelessDevice) => {
    setEditingAddon(addon);
    setAddonNameDraft(addon.name || '');
    setEditAddonNameOpen(true);
  };

  const onDeleteAddon = (addon: WirelessDevice) => {
    setDeletingAddon(addon);
    setConfirmDeleteAddonOpen(true);
  };

  const handleConfirmEditAddonName = async () => {
    if (!editingAddon || !selectedDeviceId) {
      setEditAddonNameOpen(false);
      setEditingAddon(null);
      return;
    }
    const targetUuid = editingAddon.uuid;
    const prevList = data?.wirelessAddOns?.paired_lora_device || [];
    const prevName = prevList.find((a) => a.uuid === targetUuid)?.name || '';
    setData((prev) => {
      if (!prev) return prev;
      const list = prev.wirelessAddOns?.paired_lora_device || [];
      const newList = list.map((a) => (a.uuid === targetUuid ? { ...a, name: addonNameDraft } : a));
      return { ...prev, wirelessAddOns: { ...prev.wirelessAddOns, paired_lora_device: newList } };
    });
    try {
      dispatch(setLoading(true));
      setEditAddonNameOpen(false);
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/changeLoraName`, {
        name: addonNameDraft,
        uuid: targetUuid,
      });
      setSnackbarSeverity('success');
      setSnackbarMessage('Add-on name updated');
      setSnackbarOpen(true);
      await fetchData(selectedDeviceId);
    } catch (err: any) {
      setData((prev) => {
        if (!prev) return prev;
        const list = prev.wirelessAddOns?.paired_lora_device || [];
        const newList = list.map((a) => (a.uuid === targetUuid ? { ...a, name: prevName } : a));
        return { ...prev, wirelessAddOns: { ...prev.wirelessAddOns, paired_lora_device: newList } };
      });
      setSnackbarSeverity('error');
      setSnackbarMessage(err?.message || 'Failed to update add-on name');
      setSnackbarOpen(true);
    } finally {
      setEditingAddon(null);
      dispatch(setLoading(false));
    }
  };

  const handleConfirmDeleteAddon = async () => {
    if (!selectedDeviceId || !deletingAddon) {
      setConfirmDeleteAddonOpen(false);
      setDeletingAddon(null);
      return;
    }
    const targetUuid = deletingAddon.uuid;
    const prevList = data?.wirelessAddOns?.paired_lora_device || [];
    setData((prev) => {
      if (!prev) return prev;
      const list = prev.wirelessAddOns?.paired_lora_device || [];
      const newList = list.filter((a) => a.uuid !== targetUuid);
      return { ...prev, wirelessAddOns: { ...prev.wirelessAddOns, paired_lora_device: newList } };
    });
    try {
      dispatch(setLoading(true));
      setConfirmDeleteAddonOpen(false);
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/removeLoraDevice`, { uuid: targetUuid });
      setSnackbarSeverity('success');
      setSnackbarMessage('Add-on removed');
      setSnackbarOpen(true);
      await fetchData(selectedDeviceId);
    } catch (err: any) {
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, wirelessAddOns: { ...prev.wirelessAddOns, paired_lora_device: prevList } };
      });
      setSnackbarSeverity('error');
      setSnackbarMessage(err?.message || 'Failed to remove add-on');
      setSnackbarOpen(true);
    } finally {
      setDeletingAddon(null);
      dispatch(setLoading(false));
    }
  };

  const pairWirelessDevice = async (device: WirelessDevice) => {
    if (!selectedDeviceId) {
      notify('error', 'No device selected');
      return;
    }
    try {
      dispatch(setLoading(true));
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/pairLoraDevice`, {
        uuid: device.uuid,
        type: device.type ?? 0,
      });
      notify('success', device.type === 1 ? 'Wireless button paired' : 'Add-on paired');
      setIsUnpairedOpen(false);
      await fetchData(selectedDeviceId);
    } catch (err: any) {
      notify('error', err?.message || 'Failed to pair add-on');
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="space-y-4">
      {/* 页头：左侧标题 + SSID，右侧设备选择器 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">Device Setup</h2>
        </div>
        <div className="flex items-center">
          <DeviceSelector onDeviceChange={handleDeviceChange} selectedDeviceId={selectedDeviceId || ''} />
        </div>
      </div>

      {/* Row 1：Device Information（保留或按需调整）*/}
      <Card header={<div className="flex items-center justify-between"><SectionTitle title="Device Information" /><button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => setIsDeviceInfoOpen(true)} aria-label="Edit Device Info"><FiChevronRight className="h-4 w-4" /></button></div>} className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <InlineRow label="Time" value={data?.timeInfo?.time} />
            <InlineRow label="Time Zone" value={data?.TagAndTimezone?.timezone || data?.timeInfo?.time_zone_id} />
            <InlineRow label="Device Name" value={data?.TagAndTimezone?.tag} />
            <InlineRow label="Device SSID" value={ssidText} />
          </div>
          <div>
            <InlineRow label="MAC Address" value={formatMac(selectedDeviceId || '')} />
            <InlineRow label="Network Status" value={data?.networkInfo?.status} />
            <InlineRow label="Network Name" value={data?.networkInfo?.network_name} />
            <InlineRow label="Signal" value={data?.networkInfo?.signal} />
          </div>
        </div>
      </Card>

      {/* Row 2：Paired Add-ons 独占一行 */}
      <Card header={<div className="flex items-center justify-between"><SectionTitle title="Paired Add-ons" /><button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => setIsUnpairedOpen(true)} aria-label="Add add-on"><FiPlus className="h-4 w-4" /></button></div>} className="p-3">
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Paired Wireless Expanders
            </div>
            {pairedExpanders.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {pairedExpanders.map((addon) => (
                  <div key={addon.uuid} className="flex items-center justify-between border rounded-md px-3 py-2 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center w-full">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[250px]">
                          {addon.label}
                        </span>
                      </div>
                      <div className="min-w-[220px]">
                        <div className="text-xs text-gray-500 dark:text-gray-400">Name</div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]">
                            {addon.name}
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center text-primary-600 hover:text-primary-700"
                            onClick={() => beginEditAddon(addon)}
                            aria-label="Edit name"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Connection Status</div>
                        <span className={`text-xs font-medium ${getConnectionClass(addon.connected)} px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 whitespace-nowrap`}>
                          {addon.connected ? 'Connected' : 'Disconnected'}
                        </span>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Signal Strength</div>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          <FiWifi className={getSignalClass(addon.rssi)} />
                          <span className={`text-xs ${getSignalClass(addon.rssi)}`}>{addon.rssi} dBm</span>
                        </span>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Device Id</div>
                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate whitespace-nowrap">
                          {addon.uuid}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        className="inline-flex items-center text-red-500 hover:text-red-600"
                        onClick={() => onDeleteAddon(addon)}
                        aria-label="Delete add-on"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/60 px-5 py-6">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200">No paired wireless expanders</div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Pair a power expander to see connection status and details here.
                </div>
              </div>
            )}
          </div>

          {supportsWirelessButtons && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Paired Wireless Buttons
              </div>
              {pairedButtons.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {pairedButtons.map((addon) => (
                    <button
                      key={addon.uuid}
                      type="button"
                      className="w-full rounded-md border px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 dark:border-gray-700"
                      onClick={() => {
                        setSelectedWirelessSwitch(addon);
                        setIsWirelessSwitchOpen(true);
                      }}
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Wireless Button - {addon.uuid}
                          </div>
                          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {addon.name || 'Wireless Switch'}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Connection</div>
                            <div className={`text-xs font-medium ${getConnectionClass(addon.connected)}`}>
                              {addon.connected ? 'Connected' : 'Disconnected'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Battery</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300">
                              {getWirelessBatteryPercentage(addon.battery)}%
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Buttons</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300">
                              {Math.min(Math.max(addon.btnType ?? 4, 1), 4)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Open</div>
                            <div className="text-xs text-primary-600 dark:text-primary-400">Details</div>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/60 px-5 py-6">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-200">No paired wireless buttons</div>
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Pair a wireless switch to manage button bindings and battery status.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Row 3：Pool Parameters 与 Firmware 同行 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card header={<div className="flex items-center justify-between"><SectionTitle title="Pool Parameters" /><button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => setIsPoolParamsOpen(true)} aria-label="Edit Pool Parameters"><FiChevronRight className="h-4 w-4" /></button></div>} className="p-3">
          <div className="space-y-2">
            <InlineRow label="Pool Size" value={<span>{data?.poolParams?.pool_size ?? '-'} Kilolitres</span>} />
            <InlineRow label="Electricity Cost" value={<span>{data?.poolParams?.electricity_cost ?? '-'} Cents/KWh</span>} />
          </div>
        </Card>

        <Card header={<div className="flex items-center justify-between"><SectionTitle title="Firmware" /><button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => setIsFirmwareOpen(true)} aria-label="Edit Firmware"><FiChevronRight className="h-4 w-4" /></button></div>} className="p-3">
          <div className="space-y-2">
            <InlineRow label="Firmware Version" value={data?.versionInfo?.firmware_version} />
            <InlineRow label="Latest Version" value={data?.latestFirmware?.version} />
            {isOutdated ? (
              <div className="mt-2 p-3 rounded-md border border-yellow-200 bg-yellow-50 text-sm text-gray-700 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-200">
                Your firmware is outdated. Upgrade to {latestVersion}.
              </div>
            ) : (
              <div className="mt-2 p-3 rounded-md border border-green-200 bg-green-50 text-sm text-gray-700 dark:bg-green-900/20 dark:border-green-700 dark:text-green-200">
                Your firmware is up to date.
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Row 4：Assign Equipment 放在最下面，小卡片 */}
      <Card
        header={<div className="flex items-center justify-between"><SectionTitle title="Assign Equipment" />{canShowAddEquipment && (<button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => { setAssignInitialMode('aux'); setAssignInitialSlotIndex(undefined); setIsAssignEquipmentOpen(true); }} aria-label="Add equipment"><FiPlus className="h-4 w-4" /></button>)}</div>}
        className="p-3"
      >
        {renderEquipmentCards((data?.auxAssign?.equipment || []) as unknown as EquipmentItem[], data?.pumpInfo)}
      </Card>
      {/* Device Info Sidebar */}
      <DeviceInfoSidebar
        isOpen={isDeviceInfoOpen}
        onClose={() => setIsDeviceInfoOpen(false)}
        deviceId={selectedDeviceId || ''}
        deviceName={data?.TagAndTimezone?.tag || deviceName}
        timeInfo={data?.timeInfo}
        networkInfo={data?.networkInfo}
        onSaveDeviceName={handleSaveDeviceName}
        onSaveTimeZone={(tz) => handleSaveTimeZone(tz)}
      />
      <UnpairedAddonsSidebar
        isOpen={isUnpairedOpen}
        onClose={() => setIsUnpairedOpen(false)}
        devices={filteredUnpairedAddOns}
        isWirelessButtonSupported={supportsWirelessButtons}
        onRefresh={async () => { if (selectedDeviceId) { await fetchData(selectedDeviceId); } }}
        onPairDevice={pairWirelessDevice}
      />
      <WirelessSwitchSidebar
        isOpen={isWirelessSwitchOpen}
        onClose={() => setIsWirelessSwitchOpen(false)}
        deviceId={selectedDeviceId || ''}
        addon={currentSelectedWirelessSwitch}
        auxAssign={data?.auxAssign}
        pumpInfo={data?.pumpInfo}
        onRefresh={async () => {
          if (selectedDeviceId) {
            await fetchData(selectedDeviceId);
          }
        }}
        onNotify={notify}
      />
      {/* PoolParamsSidebar */}
      <PoolParamsSidebar
        isOpen={isPoolParamsOpen}
        onClose={() => setIsPoolParamsOpen(false)}
        initialPoolSize={data?.poolParams?.pool_size}
        initialElectricityCost={data?.poolParams?.electricity_cost}
        onSave={async (size, cost) => {
          await handleSavePoolParams(size, cost);
        }}
      />
      {/* MUI Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <FirmwareUpgradeSidebar
        isOpen={isFirmwareOpen}
        onClose={() => setIsFirmwareOpen(false)}
        deviceId={selectedDeviceId || ''}
        currentVersion={currentVersion}
        latestFirmware={data?.latestFirmware}
        onUpgrade={handleUpgradeUpload}
      />
      <ConfirmDialog
        open={editAddonNameOpen}
        title="Edit Add-on Name"
        onCancel={() => setEditAddonNameOpen(false)}
        onOk={handleConfirmEditAddonName}
        cancelText="Cancel"
        okText="Save"
        fullWidth
        maxWidth="sm"
      >
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          variant="outlined"
          label="Name"
          value={addonNameDraft}
          onChange={(e) => setAddonNameDraft(e.target.value)}
          InputLabelProps={mode === 'dark' ? { sx: { color: '#fff' } } : undefined}
          sx={mode === 'dark' ? {
            '& .MuiInputBase-input': { color: '#fff' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#fff' },
              '&:hover fieldset': { borderColor: '#fff' },
              '&.Mui-focused fieldset': { borderColor: '#fff' },
            },
            '& .MuiFormLabel-root': { color: '#fff' },
          } : undefined}
        />
      </ConfirmDialog>

      {/* Delete Add-on Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDeleteAddonOpen}
        title="Confirm Delete"
        onCancel={() => setConfirmDeleteAddonOpen(false)}
        onOk={handleConfirmDeleteAddon}
        cancelText="Cancel"
        okText="Delete"
      >
        Are you sure you want to delete the add-on "{deletingAddon?.name}"?
      </ConfirmDialog>

      <AssignEquipmentSidebar
        isOpen={isAssignEquipmentOpen}
        onClose={() => setIsAssignEquipmentOpen(false)}
        deviceId={selectedDeviceId || ''}
        wirelessAddOns={data?.wirelessAddOns}
        auxAssign={data?.auxAssign}
        firmwareVersion={currentVersion}
        initialMode={assignInitialMode}
        initialSlotIndex={assignInitialSlotIndex}
        onRefresh={async () => { if (selectedDeviceId) { await fetchData(selectedDeviceId); } }}
      />
      </div>
    );
};

export default DeviceSetupPage;
